/**
 * ModalsContext - 전역 모달 상태 관리
 *
 * URL로 관리되지 않는 오버레이 모달들의 상태를 제공합니다.
 * (MapModal, SettingsModal, LogoutConfirm 등)
 */
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';

interface ModalsContextValue {
  // 모달 상태
  showMapModal: boolean;
  showSettingsModal: boolean;
  showLogoutConfirm: boolean;
  showCart: boolean;
  showNotifications: boolean;
  // 맵 모달
  openMapModal: () => void;
  closeMapModal: () => void;
  toggleMapModal: () => void;
  // 설정 모달
  openSettingsModal: () => void;
  closeSettingsModal: () => void;
  toggleSettingsModal: () => void;
  // 로그아웃 확인
  openLogoutConfirm: () => void;
  closeLogoutConfirm: () => void;
  // 장바구니
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
  // 알림
  openNotifications: () => void;
  closeNotifications: () => void;
  toggleNotifications: () => void;
  // 전체 닫기
  closeAllModals: () => void;
  // 상태 직접 설정 (하위 호환성)
  setShowMapModal: (show: boolean) => void;
  setShowSettingsModal: (show: boolean) => void;
  setShowLogoutConfirm: (show: boolean) => void;
  setShowCart: (show: boolean) => void;
  setShowNotifications: (show: boolean) => void;
}

const ModalsContext = createContext<ModalsContextValue | null>(null);

interface ModalsProviderProps {
  children: ReactNode;
}

export function ModalsProvider({ children }: ModalsProviderProps) {
  const [showMapModal, setShowMapModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);

  // 맵 모달
  const openMapModal = useCallback(() => setShowMapModal(true), []);
  const closeMapModal = useCallback(() => setShowMapModal(false), []);
  const toggleMapModal = useCallback(() => setShowMapModal(prev => !prev), []);

  // 설정 모달
  const openSettingsModal = useCallback(() => setShowSettingsModal(true), []);
  const closeSettingsModal = useCallback(() => setShowSettingsModal(false), []);
  const toggleSettingsModal = useCallback(() => setShowSettingsModal(prev => !prev), []);

  // 로그아웃 확인
  const openLogoutConfirm = useCallback(() => setShowLogoutConfirm(true), []);
  const closeLogoutConfirm = useCallback(() => setShowLogoutConfirm(false), []);

  // 장바구니
  const openCart = useCallback(() => setShowCart(true), []);
  const closeCart = useCallback(() => setShowCart(false), []);
  const toggleCart = useCallback(() => setShowCart(prev => !prev), []);

  // 알림
  const openNotifications = useCallback(() => setShowNotifications(true), []);
  const closeNotifications = useCallback(() => setShowNotifications(false), []);
  const toggleNotifications = useCallback(() => setShowNotifications(prev => !prev), []);

  // 전체 닫기
  const closeAllModals = useCallback(() => {
    setShowMapModal(false);
    setShowSettingsModal(false);
    setShowLogoutConfirm(false);
    setShowCart(false);
    setShowNotifications(false);
  }, []);

  const value = useMemo<ModalsContextValue>(
    () => ({
      showMapModal,
      showSettingsModal,
      showLogoutConfirm,
      showCart,
      showNotifications,
      openMapModal,
      closeMapModal,
      toggleMapModal,
      openSettingsModal,
      closeSettingsModal,
      toggleSettingsModal,
      openLogoutConfirm,
      closeLogoutConfirm,
      openCart,
      closeCart,
      toggleCart,
      openNotifications,
      closeNotifications,
      toggleNotifications,
      closeAllModals,
      setShowMapModal,
      setShowSettingsModal,
      setShowLogoutConfirm,
      setShowCart,
      setShowNotifications,
    }),
    [
      showMapModal,
      showSettingsModal,
      showLogoutConfirm,
      showCart,
      showNotifications,
      openMapModal,
      closeMapModal,
      toggleMapModal,
      openSettingsModal,
      closeSettingsModal,
      toggleSettingsModal,
      openLogoutConfirm,
      closeLogoutConfirm,
      openCart,
      closeCart,
      toggleCart,
      openNotifications,
      closeNotifications,
      toggleNotifications,
      closeAllModals,
    ]
  );

  return (
    <ModalsContext.Provider value={value}>
      {children}
    </ModalsContext.Provider>
  );
}

/**
 * useModals - ModalsContext 사용 훅
 */
export function useModals(): ModalsContextValue {
  const context = useContext(ModalsContext);
  if (!context) {
    throw new Error('useModals must be used within a ModalsProvider');
  }
  return context;
}

/**
 * useMapModal - 맵 모달 상태만 사용
 */
export function useMapModal() {
  const { showMapModal, openMapModal, closeMapModal, toggleMapModal, setShowMapModal } = useModals();
  return { showMapModal, openMapModal, closeMapModal, toggleMapModal, setShowMapModal };
}

/**
 * useSettingsModal - 설정 모달 상태만 사용
 */
export function useSettingsModal() {
  const { showSettingsModal, openSettingsModal, closeSettingsModal, toggleSettingsModal, setShowSettingsModal } = useModals();
  return { showSettingsModal, openSettingsModal, closeSettingsModal, toggleSettingsModal, setShowSettingsModal };
}

/**
 * useCartModal - 장바구니 모달 상태만 사용
 */
export function useCartModal() {
  const { showCart, openCart, closeCart, toggleCart, setShowCart } = useModals();
  return { showCart, openCart, closeCart, toggleCart, setShowCart };
}

/**
 * useNotificationsModal - 알림 모달 상태만 사용
 */
export function useNotificationsModal() {
  const { showNotifications, openNotifications, closeNotifications, toggleNotifications, setShowNotifications } = useModals();
  return { showNotifications, openNotifications, closeNotifications, toggleNotifications, setShowNotifications };
}
